import { useState } from "react";
import { useApp } from "@/contexts/AppContext";
import AddUserModal from "@/components/users/AddUserModal";
import EditUserModal from "@/components/users/EditUserModal";
import Badge from "@/components/common/Badge";
import Card from "@/components/common/Card";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import RowIcon from "@/components/common/RowIcon";
import ActionMenu from "@/components/common/ActionMenu";
import { usePageTitle } from "@/components/common/AppShell";
import { User, Pencil, Trash2, Plus } from "lucide-react";

const ROLE_VARIANT = {
  admin: "brand",
  user: "info",
  provider: "success",
};

export default function UsersPage() {
  usePageTitle("Users");
  const {
    users,
    usersLoading,
    usersError,
    deleteUser,
    currentUser: currentUsername,
    currentUserType,
  } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleDelete = async (uuid, username) => {
    if (window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      try {
        await deleteUser(uuid);
      } catch (error) {
        alert("Failed to delete user: " + (error.message || "Unknown error"));
      }
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const headerRow = (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <p className="text-base text-(--text-secondary)">
        Invite teammates and manage their roles.
      </p>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="inline-flex items-center gap-2 rounded-(--radius-md) border-0 bg-(--brand-primary) px-[18px] py-[10px] text-[13px] font-bold text-(--brand-primary-contrast) outline-none transition-colors hover:bg-(--brand-primary-hover) focus:outline-none"
      >
        <Plus size={16} strokeWidth={2.5} />
        Add user
      </button>
    </div>
  );

  if (usersLoading) {
    return (
      <section>
        {headerRow}
        <Card>
          <p className="text-sm text-(--text-secondary)">Loading users...</p>
        </Card>
      </section>
    );
  }

  if (usersError) {
    return (
      <section>
        {headerRow}
        <Card>
          <p className="text-sm text-(--status-danger)">
            Error loading users: {usersError.message}
          </p>
        </Card>
      </section>
    );
  }

  return (
    <section>
      {headerRow}

      <DataTable>
        <thead>
          <tr className={DATA_TABLE_CLASSES.headRow}>
            <th className={DATA_TABLE_CLASSES.th}>Username</th>
            <th className={DATA_TABLE_CLASSES.th}>Role</th>
            <th className={DATA_TABLE_CLASSES.th}>Created at</th>
            <th className={DATA_TABLE_CLASSES.thRight}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan="4" className={DATA_TABLE_CLASSES.emptyCell}>
                No users found
              </td>
            </tr>
          ) : (
            users.map((user) => {
              const isSelf = user.username === currentUsername;
              const isAdmin = currentUserType === "admin";
              const menuItems = [
                ...(isAdmin
                  ? [
                      {
                        label: "Edit",
                        icon: Pencil,
                        onClick: () => handleEdit(user),
                      },
                    ]
                  : []),
                ...(isAdmin && !isSelf
                  ? [
                      {
                        label: "Delete",
                        icon: Trash2,
                        danger: true,
                        onClick: () => handleDelete(user.uuid, user.username),
                      },
                    ]
                  : []),
              ];
              return (
                <tr key={user.uuid} className={DATA_TABLE_CLASSES.bodyRow}>
                  <td className={DATA_TABLE_CLASSES.td}>
                    <div className="flex items-center gap-3">
                      <RowIcon icon={User} />
                      <span className="font-semibold">{user.username}</span>
                    </div>
                  </td>
                  <td className={DATA_TABLE_CLASSES.td}>
                    <Badge variant={ROLE_VARIANT[user.role] ?? "neutral"}>
                      {user.role}
                    </Badge>
                  </td>
                  <td
                    className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                  >
                    {new Date(user.createdAt).toLocaleString("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </td>
                  <td className={DATA_TABLE_CLASSES.tdRight}>
                    {menuItems.length > 0 ? (
                      <ActionMenu
                        ariaLabel={`Actions for ${user.username}`}
                        items={menuItems}
                      />
                    ) : null}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </DataTable>

      <AddUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />
    </section>
  );
}
